import { base64Decode, base64Encode } from '@Core/utils';
import { EmailAccountConnectorMessageModel } from './email-account-connector-message.model';
import { NylasMessageModel } from './nylas-message.model';

export class NylasLocalStorageMessageModel {
    static readonly localStorageKey = 'RecentSentNylasMessages';
    static readonly delimiter = '%%';
    private static readonly hashFields = [
        'grantId',
        'accountId',
        'fromEmail',
        'toUuid',
        'subject',
        'date'
    ];
    private static readonly hashFieldConverters = {
        'grantId': (id: string) => Number(id),
        'accountId': (id: string) => Number(id),
        'fromEmail': (str: string) => str,
        'toUuid': (str: string) => str,
        'subject': (str: string) => str,
        'date': (date: string) => new Date(date)
    };

    grantId: number;
    fromEmail: string;
    toUuid: string;
    date: Date;
    deliveryId: number;
    accountId: number;
    subject: string;

    static fromNylasMessageModel(message: NylasMessageModel, toUuid: string, accountId: number): NylasLocalStorageMessageModel {
        const nylasMessage = new NylasLocalStorageMessageModel();
        nylasMessage.grantId = message.grantId;
        nylasMessage.fromEmail = message.fromEmail;
        nylasMessage.toUuid = toUuid;
        nylasMessage.subject = message.subject;
        nylasMessage.date = message.date;
        nylasMessage.accountId = accountId;
        return nylasMessage;
    }

    static fromEmailAccountConnectorMessage(message: EmailAccountConnectorMessageModel, toUuid: string, accountId: number): NylasLocalStorageMessageModel {
        const nylasMessage = new NylasLocalStorageMessageModel();
        nylasMessage.grantId = message.sendFrom?.grantId;
        nylasMessage.fromEmail = message.fromEmail;
        nylasMessage.toUuid = toUuid;
        nylasMessage.subject = message.subjectLine;
        nylasMessage.date = new Date();
        nylasMessage.accountId = accountId;
        return nylasMessage;
    }

    static parseLocalStorageHash(hash: string): NylasLocalStorageMessageModel {
        const decoded = base64Decode(hash);
        const fields = decoded.split(NylasLocalStorageMessageModel.delimiter);

        const message = new NylasLocalStorageMessageModel();
        for (let i = 0; i < fields.length; i++) {
            const hashField = this.hashFields[i];
            const value = this.hashFieldConverters[hashField](fields[i]);
            message[hashField] = value;
        }
        return message;
    }

    compareMessages(messageB: NylasLocalStorageMessageModel): boolean {
        return (
            this.grantId === messageB.grantId
            && this.fromEmail === messageB.fromEmail
            && this.toUuid === messageB.toUuid
            && this.accountId === messageB.accountId
            && this.subject === messageB.subject
        );
    }

    localStorageHash(): string {
        const hashValues = NylasLocalStorageMessageModel.hashFields.map(field => this[field]);
        const plainHash = hashValues.join(NylasLocalStorageMessageModel.delimiter);
        return base64Encode(plainHash);
    }
}

export class NylasMessageLocalStorage {
    static readonly localStorageKey = 'RecentSentNylasMessages';

    // 2 hours in MS
    private readonly messageExpiryLimitMs = 2 * 60 * 60 * 1000;

    private accountIdToMessageArrayMap: Map<string, NylasLocalStorageMessageModel[]> = new Map();

    constructor() {
        this.populateArrayMap();
    }

    saveLocalStorage() {
        const valueArrays = this.accountIdToMessageArrayMap.values();
        const allValues: NylasLocalStorageMessageModel[] = [];
        for (const valueArray of valueArrays) {
            allValues.push(...valueArray);
        }
        const allHashes = allValues.map(value => value.localStorageHash());
        localStorage.setItem(NylasLocalStorageMessageModel.localStorageKey, JSON.stringify(allHashes));
    }

    getLocalStorageNylasMessagesForAccount(accountId: number): NylasLocalStorageMessageModel[] {
        return this.accountIdToMessageArrayMap.get(accountId.toString())?.slice() ?? [];
    }

    setLocalStorageNylasMessagesForAccount(accountId: number, messages: NylasLocalStorageMessageModel[]) {
        this.accountIdToMessageArrayMap.set(accountId.toString(), messages);
    }

    addMessageToLocalStorage(message: NylasLocalStorageMessageModel) {
        this.setValueOnMap(message);
    }

    private getLocalStorageNylasMessages(): string[] {
        const currentStorage = localStorage.getItem(NylasLocalStorageMessageModel.localStorageKey);
        if (currentStorage === null) {
            return [];
        }
        return JSON.parse(currentStorage);
    }

    private populateArrayMap() {
        const storageStrings = this.getLocalStorageNylasMessages();
        for (const storageString of storageStrings) {
            const message = NylasLocalStorageMessageModel.parseLocalStorageHash(storageString);
            this.setValueOnMap(message);
        }
    }

    private setValueOnMap(message: NylasLocalStorageMessageModel) {
        if (this.messageIsExpired(message)) {
            return;
        }

        const accountArray = this.accountIdToMessageArrayMap.get(message.accountId.toString());
        if (!accountArray) {
            this.accountIdToMessageArrayMap.set(message.accountId.toString(), [message]);
        } else {
            // TODO: Check uniqueness for message
            accountArray.push(message);
        }
    }

    private messageIsExpired(message: NylasLocalStorageMessageModel): boolean {
        const messageDate = message?.date;
        if (!messageDate) {
            return true;
        }
        return (new Date().getTime() - messageDate.getTime() > this.messageExpiryLimitMs);
    }
}
